import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { colors, fontFamily, fontSize, radius, space } from '../tokens'
import { trpc } from '../trpc'
import { ErrorNotice } from './ErrorNotice'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Props = {
  bandCheckId: string
}

// Secondary, low-prominence capture for users who aren't paying right now.
// The free verdict view is already a complete preview (redacted but
// self-contained), so the email isn't delivering anything new — it's a
// bookmark + 14/60/100-day nudge. Styling reflects that: smaller block,
// muted colours, less competition with the £4.99 CTA above.
export const EmailCapture = ({ bandCheckId }: Props) => {
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const capture = trpc.bandCheck.captureEmail.useMutation()

  if (capture.isSuccess) {
    return (
      <View style={[styles.box, styles.boxDone]}>
        <Text style={styles.title}>Saved — we'll nudge you</Text>
        <Text style={styles.subtitle}>
          We've saved your check. You'll get the link in your inbox and a brief check-in at 14, 60,
          and 100 days. Unsubscribe link in every email.
        </Text>
      </View>
    )
  }

  const emailValid = EMAIL.test(email.trim())
  const showEmailError = emailTouched && email.trim().length > 0 && !emailValid
  const canSubmit = emailValid && !capture.isPending

  return (
    <View style={styles.box}>
      <Text style={styles.title}>Not ready yet? Save your check</Text>
      <Text style={styles.subtitle}>
        We'll email you the link to come back to and check in at 14, 60, and 100 days. No marketing,
        unsubscribe any time.
      </Text>
      <TextInput
        accessibilityLabel="Email"
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect={false}
        editable={!capture.isPending}
        keyboardType="email-address"
        onBlur={() => setEmailTouched(true)}
        onChangeText={setEmail}
        placeholder="you@example.com"
        style={[styles.input, showEmailError && styles.inputError]}
        textContentType="emailAddress"
        value={email}
      />
      {showEmailError ? (
        <Text style={styles.fieldError}>Enter a valid email, e.g. you@example.com.</Text>
      ) : null}
      <Pressable
        accessibilityRole="button"
        disabled={!canSubmit}
        onPress={() => capture.mutate({ bandCheckId, email: email.trim() })}
        style={[styles.button, !canSubmit && styles.buttonDisabled]}
      >
        <Text style={styles.buttonText}>{capture.isPending ? 'Saving…' : 'Save my check'}</Text>
      </Pressable>
      <ErrorNotice error={capture.error} />
    </View>
  )
}

const styles = StyleSheet.create({
  box: {
    marginTop: space[16],
    padding: space[16],
    borderRadius: radius.sm,
    backgroundColor: colors.gray[50],
    borderWidth: 1,
    borderColor: colors.gray[300],
    gap: space[4],
  },
  boxDone: { backgroundColor: colors.success[100], borderColor: colors.success[700] },
  title: {
    fontSize: fontSize.small,
    fontWeight: '600',
    color: colors.gray[900],
    fontFamily: fontFamily.body,
  },
  subtitle: {
    fontSize: fontSize.tiny,
    color: colors.gray[700],
    fontFamily: fontFamily.body,
    lineHeight: 18,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.gray[300],
    borderRadius: radius.sm,
    paddingHorizontal: 12,
    paddingVertical: space[8],
    fontSize: fontSize.small,
    backgroundColor: 'white',
    marginTop: space[4],
    fontFamily: fontFamily.body,
  },
  button: {
    marginTop: space[4],
    backgroundColor: colors.gray[300],
    paddingVertical: 10,
    borderRadius: radius.sm,
    alignItems: 'center',
  },
  buttonDisabled: { opacity: 0.5 },
  buttonText: {
    color: colors.gray[900],
    fontSize: fontSize.small,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  error: { color: colors.error[900], fontSize: fontSize.tiny, marginTop: space[4] },
  inputError: { borderColor: colors.error[700], borderWidth: 2 },
  fieldError: { color: colors.error[900], fontSize: fontSize.tiny, marginTop: space[4] },
})
